import React, { useEffect, useRef, useState } from 'react';
import { BarChart2, Loader2, AlertCircle, ClipboardList, Download } from 'lucide-react';
import {
  TabContentWrapper,
  ExtractionHeader,
  ExtractionTitle,
  ExtractedBadge,
  StatusMessage,
  ExtractButton,
  LoadingState,
  EmptyExtractionState,
  AnalysisItemGrid,
  AnalysisItemCard,
  AnalysisItemHeader,
  AnalysisItemTitle,
  AnalysisScoreBadge,
  AnalysisProgressTrack,
  AnalysisProgressBar,
  AnalysisItemResponse,
} from './processing.styles';

import { useAnalysis } from './hook/Useprocessing';
import generateAnalysisReportPDF from './reports/AnalysisReport';

const Analysis = ({ listId }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const reportRef = useRef(null);
  const { analyseMutation, analysisQuery, isComplete, trigger } = useAnalysis(listId);

  useEffect(() => {
    if (listId) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  const analysisData = analysisQuery.data?.analysis ?? [];
  const hasAnalysisData = analysisData.length > 0;

  const handleCheckAgain = () => trigger();

  const handleDownloadReport = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await generateAnalysisReportPDF({
        node: reportRef.current,
        listId,
        analysisData,
        fileName: `analysis-report-${listId}.pdf`,
      });
    } catch (err) {
      console.error('Failed to generate analysis report:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // ── First call in flight ──────────────────────────────────────────────────
  if (analyseMutation.isPending && !analysisQuery.data) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Analysing documents…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── analyseData() failed outright ────────────────────────────────────────
  if (analyseMutation.isError && !analysisQuery.data) {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <AlertCircle size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Analysis failed</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {analyseMutation.error?.message || 'Something went wrong. Please try again.'}
          </p>
          <ExtractButton type="button" onClick={handleCheckAgain}>
            <BarChart2 size={14} /> Try Again
          </ExtractButton>
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Fetching the actual analysis data once marked complete ──────────────
  if (isComplete && analysisQuery.isLoading) {
    return (
      <TabContentWrapper>
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Fetching analysis data…</span>
        </LoadingState>
      </TabContentWrapper>
    );
  }

  // ── Data arrived ──────────────────────────────────────────────────────────
  if (hasAnalysisData) {
    return (
      <TabContentWrapper>
        <ExtractionHeader>
          <ExtractionTitle>Analysis Result</ExtractionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ExtractedBadge>
              <BarChart2 size={14} />
              Analysis Complete
            </ExtractedBadge>
            <ExtractButton type="button" onClick={handleDownloadReport} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <Loader2 size={14} className="spin" /> Generating…
                </>
              ) : (
                <>
                  <Download size={14} /> Download Report
                </>
              )}
            </ExtractButton>
          </div>
        </ExtractionHeader>

        <AnalysisItemGrid ref={reportRef}>
          {analysisData.map((item) => (
            <AnalysisItemCard key={item._id}>
              <AnalysisItemHeader>
                <AnalysisItemTitle>{item.title}</AnalysisItemTitle>
                <AnalysisScoreBadge $score={item.score}>{item.score}%</AnalysisScoreBadge>
              </AnalysisItemHeader>

              <AnalysisProgressTrack>
                <AnalysisProgressBar $score={item.score} />
              </AnalysisProgressTrack>

              <AnalysisItemResponse>{item.response}</AnalysisItemResponse>
            </AnalysisItemCard>
          ))}
        </AnalysisItemGrid>
      </TabContentWrapper>
    );
  }

  // ── Not complete yet — pending state, "Check Again" re-triggers ─────────
  if (!isComplete) {
    return (
      <TabContentWrapper>
        <EmptyExtractionState>
          <ClipboardList size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Analysis In Progress</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            Your documents are still being analysed. Check back shortly or try again.
          </p>
          <ExtractButton
            type="button"
            onClick={handleCheckAgain}
            disabled={analyseMutation.isPending}
          >
            {analyseMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Checking…
              </>
            ) : (
              <>
                <BarChart2 size={14} /> Check Again
              </>
            )}
          </ExtractButton>
        </EmptyExtractionState>
      </TabContentWrapper>
    );
  }

  // ── Query-level error ─────────────────────────────────────────────────────
  if (analysisQuery.isError) {
    return (
      <TabContentWrapper>
        <StatusMessage $type="error" style={{ margin: '1.5rem 0' }}>
          <AlertCircle size={14} />
          {analysisQuery.error?.message || 'Failed to load analysis data.'}
        </StatusMessage>
      </TabContentWrapper>
    );
  }

  // ── Complete but empty ────────────────────────────────────────────────────
  return (
    <TabContentWrapper>
      <EmptyExtractionState>
        <ClipboardList size={36} strokeWidth={1.2} />
        <p style={{ margin: 0 }}>No analysis data available yet.</p>
      </EmptyExtractionState>
    </TabContentWrapper>
  );
};

export default Analysis;
